import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, Checkbox, Dropdown, Empty, Flex, Spin, Table, Tag, Typography } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { getPipelineCandidates } from '../../../../services/dropdownApi';
import CustomPagination from '../../../../components/CustomPagination';
import unorderedListOutlinedIcon from '../../../../components/images/common/unorderedlistoutlined.svg';

const PIPELINE_ACTIONS = [
  { key: 'quick-submit', label: 'Quick Submit', route: '/popup/quick-submit', maxSelect: 1 },
  { key: 'shortlist',    label: 'Shortlist',    route: '/popup/quick-submit', popupState: { defaultStage: 'Shortlisted' } },
  { key: 'tag-to-job',   label: 'Tag to Jobs',  route: '/popup/tag-to-job' },
  { key: 'mail',         label: 'Send Email',   route: '/popup/mail' },
];

const { Text } = Typography;

const DEFAULT_PAGE_SIZE = 50;
const MIN_VISIBLE = 6;

function nameValue(r) {
  return `${r.firstName || ''} ${r.lastName || ''}`.trim() || '—';
}

const ALL_COLUMNS = [
  {
    key: 'name',
    label: 'Name',
    render: (_, r) => (
      <span className="job-cell-content">
        <span className="job-cell-main-line">
          <span className="job-cell-link job-cell-value">{nameValue(r)}</span>
        </span>
      </span>
    ),
    sorter: (a, b) => nameValue(a).localeCompare(nameValue(b)),
  },
  {
    key: 'designation',
    label: 'Designation',
    dataIndex: 'designation',
    render: v => (
      <span className="job-cell-content">
        <span className="job-cell-main-line">
          <Text className="job-cell-primary job-cell-value">{v || '—'}</Text>
        </span>
      </span>
    ),
    sorter: (a, b) => (a.designation || '').localeCompare(b.designation || ''),
  },
  {
    key: 'location',
    label: 'Location',
    dataIndex: 'currentLocation',
    render: v => (
      <span className="job-cell-content">
        <span className="job-cell-main-line">
          <Text className="job-cell-primary job-cell-value">{v || '—'}</Text>
        </span>
      </span>
    ),
    sorter: (a, b) => (a.currentLocation || '').localeCompare(b.currentLocation || ''),
  },
  {
    key: 'experience',
    label: 'Experience',
    dataIndex: 'totalYearsExperience',
    render: v => (
      <span className="job-cell-content">
        <span className="job-cell-main-line">
          <Text className="job-cell-primary job-cell-value">{v ? `${v} yrs` : '—'}</Text>
        </span>
      </span>
    ),
  },
  {
    key: 'workAuth',
    label: 'Work Auth',
    dataIndex: 'workAuthorization',
    render: v => (
      <span className="job-cell-content">
        <span className="job-cell-main-line">
          <Text className="job-cell-primary job-cell-value">{v || '—'}</Text>
        </span>
      </span>
    ),
  },
  {
    key: 'source',
    label: 'Source',
    dataIndex: 'source',
    render: v => (
      <span className="job-cell-content">
        <span className="job-cell-main-line">
          <Text className="job-cell-primary job-cell-value">{v || '—'}</Text>
        </span>
      </span>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    dataIndex: 'status',
    render: v => (
      <span className="job-cell-content">
        <span className="job-cell-main-line">
          {v ? <Tag className="job-cell-badge job-cell-value">{v}</Tag> : <Text className="job-cell-primary job-cell-value">—</Text>}
        </span>
      </span>
    ),
  },
  {
    key: 'ownership',
    label: 'Ownership',
    dataIndex: 'ownership',
    render: v => (
      <span className="job-cell-content">
        <span className="job-cell-main-line">
          <Text className="job-cell-primary job-cell-value">{v || '—'}</Text>
        </span>
      </span>
    ),
  },
];

export default function PipelineTab({ jobId, onCountChange }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading]             = useState(false);
  const [data, setData]                   = useState([]);
  const [total, setTotal]                 = useState(0);
  const [page, setPage]                   = useState(1);
  const [pageSize, setPageSize]           = useState(DEFAULT_PAGE_SIZE);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows]   = useState([]);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(ALL_COLUMNS.map(c => c.key));
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);

  const menuItems = PIPELINE_ACTIONS
    .filter((action) => !action.maxSelect || selectedRowKeys.length <= action.maxSelect)
    .map((action) => ({
      key: action.key,
      label: action.label,
      danger: action.danger ?? false,
      onClick: () => {
        navigate(action.route, {
          state: {
            background: location,
            selectedIds: selectedRowKeys,
            selectedRecords: selectedRows,
            moduleName: 'candidate',
            ...(action.popupState ?? {}),
          },
        });
      },
    }));

  const fetchData = async (pageNum = 1, size = pageSize) => {
    if (!jobId) return;
    setLoading(true);
    try {
      const offset = (pageNum - 1) * size;
      const res    = await getPipelineCandidates({ jobId, offset, limit: size });
      const list   = res?.data?.data ?? [];
      const count  = res?.data?.count ?? 0;
      setData(list);
      setTotal(count);
      onCountChange?.(count);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { setPage(1); fetchData(1, pageSize); }, [jobId]);

  const visibleColumns = ALL_COLUMNS.filter(c => visibleColumnKeys.includes(c.key));
  const allVisible     = visibleColumnKeys.length === ALL_COLUMNS.length;
  const someVisible    = visibleColumnKeys.length > 0 && !allVisible;

  const toggleColumn = (key, checked) => {
    setVisibleColumnKeys(prev => {
      if (checked) return prev.includes(key) ? prev : [...prev, key];
      if (prev.length <= MIN_VISIBLE) return prev;
      return prev.filter(k => k !== key);
    });
  };

  const columnVisibilityContent = (
    <div className="column-visibility-menu" onClick={e => e.stopPropagation()}>
      <Checkbox
        checked={allVisible}
        indeterminate={someVisible}
        onChange={e => setVisibleColumnKeys(
          e.target.checked ? ALL_COLUMNS.map(c => c.key) : ALL_COLUMNS.slice(0, MIN_VISIBLE).map(c => c.key)
        )}
      >
        Select All
      </Checkbox>
      {ALL_COLUMNS.map(col => (
        <Checkbox
          key={col.key}
          checked={visibleColumnKeys.includes(col.key)}
          disabled={visibleColumnKeys.includes(col.key) && visibleColumnKeys.length <= MIN_VISIBLE}
          onChange={e => toggleColumn(col.key, e.target.checked)}
        >
          {col.label}
        </Checkbox>
      ))}
    </div>
  );

  return (
    <div className="antd dynamic-list-view">
      <Card>
        <Flex align="center" gap={3}>
          <Dropdown
            open={columnMenuOpen}
            onOpenChange={setColumnMenuOpen}
            trigger={['click']}
            dropdownRender={() => columnVisibilityContent}
            placement="bottomLeft"
            overlayClassName="column-visibility-dropdown"
          >
            <Button
              aria-label="Display columns"
              type="text"
              icon={<img src={unorderedListOutlinedIcon} alt="" className="job-action-list-icon" />}
              size="small"
              className="job-actions-button job-column-display-button"
            >
              <DownOutlined className="job-actions-caret" />
            </Button>
          </Dropdown>
          {selectedRowKeys.length > 0 && (
            <>
              <Dropdown menu={{ items: menuItems }} trigger={['click']}>
                <Button type="text" size="small" className="job-actions-button job-actions-menu-button">
                  Actions <DownOutlined className="job-actions-caret" />
                </Button>
              </Dropdown>
              <Text className="job-selected-count">
                Selected ({selectedRowKeys.length})
              </Text>
            </>
          )}
        </Flex>
      </Card>

      <Spin spinning={loading}>
        <Table
          rowSelection={{
            type: 'checkbox',
            selectedRowKeys,
            onChange: (keys, rows) => { setSelectedRowKeys(keys); setSelectedRows(rows); },
          }}
          columns={visibleColumns}
          dataSource={data}
          size="middle"
          showSorterTooltip={false}
          tableLayout="fixed"
          pagination={false}
          className="job-list-table"
          rowKey={r => r._id || r.email || Math.random()}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Data" /> }}
        />
      </Spin>

      <CustomPagination
        current={page}
        pageSize={pageSize}
        total={total}
        onChange={p => { setPage(p); fetchData(p, pageSize); }}
        onPageSizeChange={size => { setPageSize(size); setPage(1); fetchData(1, size); }}
      />
    </div>
  );
}
